import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import constructionBackgroundImg from '../assets/images/ConstructionEducationBackground.webp';
import BackButton from '../components/common/BackButton';

// Add type definitions
type Topic = string[];
type Category = Record<string, Topic>;
type RoadmapData = Record<string, Category>;

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 2rem;
  background: url(${constructionBackgroundImg});
  background-size: 100% auto;
  background-position: top center;
  background-repeat: repeat-y;
  color: #ffffff;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #9575cd;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
`;

const RoadmapContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const TierContainer = styled.div`
  background: rgba(0, 0, 0, 0.6);
  border-radius: 10px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(149, 117, 205, 0.2);

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: translateY(-5px);
    border-color: rgba(149, 117, 205, 0.4);
  }
`;

const TierTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #9575cd;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CategoryCard = styled(motion.div)`
  background: rgba(0, 0, 0, 0.6);
  border-radius: 10px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(149, 117, 205, 0.2);

  &:hover {
    background: rgba(0, 0, 0, 0.7);
    transform: translateY(-5px);
    border-color: rgba(149, 117, 205, 0.4);
  }
`;

const CategoryTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #9575cd;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
`;

const TopicsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TopicItem = styled.li`
  margin-bottom: 0.5rem;
  padding: 0.5rem;
  background: rgba(149, 117, 205, 0.1);
  border-radius: 5px;
  transition: all 0.2s ease;
  color: #ffffff;
  border: 1px solid rgba(149, 117, 205, 0.2);
  cursor: pointer;

  &:hover {
    background: rgba(149, 117, 205, 0.2);
    border-color: rgba(149, 117, 205, 0.4);
  }
`;

const roadmapData: RoadmapData = {
  "Pre-Construction & Planning": {
    "Project Initiation": [
      "Land Acquisition",
      "Notice to Proceed (NTP)",
      "Pre-Construction Services",
      "Design Review",
      "Constructability Analysis",
      "Early Budgeting",
      "Site Logistics Planning",
      "Temporary Facilities Setup"
    ],
    "Documentation & Plans": [
      "Blueprint Reading & Interpretation",
      "Specification Interpretation",
      "IFC Set (Issued For Construction)",
      "Details",
      "Cross Section",
      "Document Control",
      "As-Built Documentation",
      "Shop Drawing Review"
    ],
    "Contract & Procurement": [
      "Bidding Process",
      "Scopes of Work",
      "Contracts",
      "City Funded Agreements",
      "Purchase Orders",
      "Suppliers & Vendors",
      "Subcontractor Agreements",
      "Material Lead Times"
    ],
    "Permitting & Approvals": [
      "Permitting Process",
      "Building Codes & Standards",
      "IBC/IRC Standards",
      "Local Amendments",
      "Fire Rating",
      "ADA Standards",
      "Zoning Requirements",
      "Environmental Permits"
    ]
  },
  "Site Development": {
    "Earthwork & Grading": [
      "Earthwork",
      "Grading",
      "Cut & Fills",
      "Drainage and Slopes",
      "Geotech Reports",
      "Soil Classifications",
      "Dewatering Site",
      "Compaction Testing",
      "Erosion Control"
    ],
    "Site Utilities": [
      "Utility Coordination",
      "Storm Drain / Catch Basin",
      "Gravity Lines",
      "Water Meter / Water Vault",
      "Fire Hydrant",
      "Energy Provider and Distribution",
      "Telecom",
      "Utility Trenching",
      "Backflow Prevention"
    ],
    "Site Layout & Control": [
      "Layout",
      "Surveying / Staking",
      "Property Lines & Plats",
      "Easements",
      "Benchmark Establishment",
      "Grid System Setup"
    ],
    "Environmental Management": [
      "SWPPP Management",
      "Erosion Control",
      "Trash Removal",
      "Dust Control",
      "Hazardous Material Handling"
    ]
  },
  "Structural Systems": {
    "Foundations": [
      "Foundation Types",
      "Footings",
      "Piers",
      "Grade Beams",
      "Mudsill",
      "Underpinning",
      "Soil Bearing Capacity",
      "Foundation Waterproofing"
    ],
    "Concrete Work": [
      "Concrete Fundamentals",
      "Concrete Plant",
      "Concrete Mix Designs",
      "Yardages",
      "Formwork",
      "Reinforcement",
      "Testing",
      "Gypcrete / Lightweight Concrete",
      "Horizontal Concrete Blocking",
      "Control Joint / Expansion Joint"
    ],
    "Wood Framing": [
      "Wall Framing / Bracing",
      "Framing Components",
      "Posts / Columns",
      "Floor Framing",
      "Roof Framing",
      "Trusses",
      "Draft Stop / Fire Blocking",
      "Connectors",
      "Kicker"
    ],
    "Steel & Masonry": [
      "Structural Steel",
      "Masonry",
      "Welding",
      "Lintel",
      "Steel Connections",
      "Masonry Anchors"
    ]
  },
  "Building Envelope": {
    "Roofing": [
      "Roofing Systems & Materials",
      "Decking",
      "Scupper Drain / Gutters",
      "Soffit / Fascia / Overhangs",
      "Roof Penetrations",
      "Ice & Water Shield"
    ],
    "Exterior Walls": [
      "Sheathing",
      "Building Wrap & Envelope",
      "Insulation",
      "Siding",
      "Fiber Cement",
      "Masonry",
      "Plaster / Stucco",
      "Corner Beads",
      "Expansion Joints"
    ],
    "Fenestration": [
      "Windows",
      "Doors",
      "Installation",
      "Jambs",
      "Casing",
      "Hinge",
      "Door Strike",
      "Trim",
      "Weatherstripping"
    ],
    "Waterproofing": [
      "Waterproofing Systems",
      "Flashing",
      "Integration Points",
      "Caulking",
      "Sealants"
    ],
    "Envelope Completion": [
      "Drying-in (Milestone)",
      "Air Barrier Testing",
      "Water Testing"
    ]
  },
  "MEP Systems": {
    "Plumbing": [
      "Water Heater / Water Meter",
      "Piping Materials",
      "DWV System",
      "Installation Stages",
      "Connections",
      "IPC Standards",
      "Fixture Installation",
      "Backflow Prevention"
    ],
    "HVAC": [
      "Air Distribution",
      "Equipment",
      "Performance",
      "Ventilation",
      "IMC Standards",
      "Ductwork Installation",
      "Equipment Pads"
    ],
    "Electrical": [
      "Power Distribution",
      "Service & Distribution",
      "Installation",
      "Devices & Fixtures",
      "Finishing & Testing",
      "NEC / IEC Standards",
      "Panel Installation",
      "Grounding Systems"
    ],
    "Low Voltage & Fire Protection": [
      "Low Voltage Systems",
      "Fire Alarms",
      "Emergency Lighting",
      "Telecom",
      "Fire Sprinkler Systems",
      "Fire Riser",
      "Smoke Detectors",
      "Emergency Power"
    ],
    "MEP Coordination": [
      "Chase Walls",
      "Penetrations",
      "Hangers / Supports",
      "Clearance Requirements",
      "Access Panels"
    ]
  },
  "Interior Construction": {
    "Framing & Support": [
      "Interior Wall Framing",
      "Furr Out / Furr Down",
      "Ceiling Systems",
      "Wall Bracing",
      "Sound Isolation"
    ],
    "Insulation & Drywall": [
      "Insulation",
      "Drywall",
      "Backerboard",
      "Acoustic Insulation",
      "Fire-Rated Assemblies"
    ],
    "Finishes": [
      "Paint",
      "Flooring",
      "Tile",
      "Floor Underlayment",
      "Plaster",
      "Trim / Casing",
      "Wall Coverings",
      "Baseboards"
    ],
    "Specialties": [
      "Cabinetry / Millwork",
      "Appliances",
      "Stairs / Landings",
      "Ramps / Railing",
      "Accessories",
      "Signage"
    ]
  },
  "Project Management": {
    "Scheduling & Progress": [
      "Construction Sequencing & Phasing",
      "Critical Path Method",
      "Software Tools",
      "Look-Ahead Planning",
      "Project Completion Time Estimation",
      "Daily Reporting & Documentation",
      "Subcontractor Scheduling",
      "Material Delivery Tracking"
    ],
    "Cost Management": [
      "Cost Code",
      "Line Items",
      "Budget Management",
      "Labor & Material Tracking",
      "Change Order Management",
      "Value Engineering"
    ],
    "Quality & Safety": [
      "Quality Control & Assurance",
      "Industry Standards",
      "Inspection Processes",
      "Checklist Management",
      "Repairs",
      "Site Safety Management",
      "OSHA Standards",
      "Site-Specific Safety Plans",
      "Toolbox Talks",
      "Incident Reporting",
      "Trenching Safety"
    ],
    "Documentation & Communication": [
      "Submittal & RFI Processes",
      "Shop Drawing Review",
      "Document Control",
      "Trade Coordination & Communication",
      "Meeting Minutes",
      "Progress Photos"
    ]
  },
  "Closeout & Turnover": {
    "Final Inspections": [
      "Building Official & Inspectors",
      "3rd Party Inspections",
      "Punch Work / Punch List",
      "Management Walks",
      "System Testing",
      "Final Walkthrough"
    ],
    "Completion & Handover": [
      "Substantial Completion",
      "Certificate of Occupancy",
      "Project Completion & Turnover",
      "Closeout",
      "Warranty Process",
      "Owner Training"
    ],
    "Documentation & Training": [
      "Final Documentation",
      "Financial Settlement",
      "Training & Handover",
      "O&M Manuals",
      "Warranty Documentation"
    ]
  },
  "Advanced Topics": {
    "Business & Legal": [
      "Estimating & Bidding",
      "Value Engineering",
      "Advanced Contract Law",
      "Texas Lien Laws",
      "Business Development",
      "Insurance Requirements",
      "Bonding"
    ],
    "Leadership & Management": [
      "Leadership & Team Management",
      "Financial Management",
      "Problem Solving & Conflict Resolution",
      "Team Building",
      "Performance Metrics"
    ],
    "Technology & Innovation": [
      "Construction Technology",
      "Bluebeam",
      "CAD",
      "Project Management Tools",
      "BIM Awareness",
      "Lean Construction",
      "Drones & Surveying",
      "3D Scanning"
    ],
    "Technical Expertise": [
      "Advanced Building Science",
      "Soil Mechanics",
      "Advanced Scheduling",
      "Materials & Tools",
      "Field Techniques & Terminology",
      "Building Performance",
      "Energy Efficiency"
    ]
  }
};

const ConstructionPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    location.state?.selectedCategory || null
  );

  useEffect(() => {
    if (location.state?.selectedCategory && location.state?.autoExpand) {
      setSelectedCategory(location.state.selectedCategory);
    }
  }, [location.state]);

  const handleBackToEducation = () => {
    navigate('/education');
  };

  const handleCategoryClick = (category: string) => {
    setSelectedCategory(category);
  };

  const handleTopicClick = (topic: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (topic === "Earthwork") {
      navigate('/education/construction/earthwork');
    }
  };

  return (
    <PageContainer>
      <BackButton onClick={handleBackToEducation} />
      <Title>Construction Learning Roadmap</Title>
      <RoadmapContainer>
        {Object.entries(roadmapData).map(([tier, categories]) => (
          <TierContainer key={tier}>
            <TierTitle>{tier}</TierTitle>
            <CategoriesContainer>
              {Object.entries(categories).map(([category, topics]) => (
                <CategoryCard
                  key={category}
                  onClick={() => handleCategoryClick(category)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <CategoryTitle>{category}</CategoryTitle>
                  {selectedCategory === category && (
                    <TopicsList>
                      {topics.map((topic) => (
                        <TopicItem 
                          key={topic}
                          onClick={(e) => handleTopicClick(topic, e)}
                        >
                          {topic}
                        </TopicItem>
                      ))}
                    </TopicsList>
                  )}
                </CategoryCard>
              ))}
            </CategoriesContainer>
          </TierContainer>
        ))}
      </RoadmapContainer>
    </PageContainer>
  );
};

export default ConstructionPage; 